import { z } from 'zod';
import { normaliseEmail } from './duplicates.js';
/**
 * The message feed (TS-1892, epic TS-1891, plan `.claude/plans/messages.md`): Message Hub keeps
 * every text (Twilio and Quo: SMS, MMS, WhatsApp, RCS) and every client email in Supabase, and a
 * Supabase `pg_cron` job (`scripts/message-hub/supabase-feed.sql`) posts new and changed rows to
 * the CRM every minute with a CRM API key holding `message.ingest`.
 *
 * The rows arrive in Message Hub's own column names (snake_case), unlike the rest of this API:
 * the job sends `row_to_json` of the `hub_*` tables, so a column added there needs no change in
 * the SQL and the CRM decides here what it keeps. Unknown keys are dropped. Times are Postgres
 * `timestamptz` text, with microseconds; `row_updated_at` is compared as Postgres prints it, never
 * through a JS Date (milliseconds), so two versions saved in the same millisecond still order.
 */
/** Rows per request. The job sends at most 500 texts or 100 emails; the cap leaves room. */
export const MESSAGE_FEED_MAX_ROWS = 1000;
/**
 * An instant Postgres can store and the feed can order: ISO 8601 with an offset, between 2000 and
 * 2100 (the regex alone lets year 0000 through, which Postgres refuses: a refused write would stall
 * the batch; review of PR #108).
 */
const ts = z.iso.datetime({ offset: true }).refine((v) => {
    const t = Date.parse(v);
    return Number.isFinite(t) && t >= Date.UTC(2000, 0, 1) && t < Date.UTC(2100, 0, 1);
}, { message: 'outside 2000-2100' });
const text = z.string().nullish();
const int = z.number().int().nullish();
/** One `hub_messages` row: a text message, keyed by the provider's id (`sid`). */
export const feedTextRowSchema = z.object({
    sid: z.string().min(1).max(200),
    provider: z.string().min(1).max(50).nullish(),
    account_sid: text,
    channel: z.string().min(1).max(50),
    direction: z.enum(['inbound', 'outbound']),
    twilio_direction: text,
    contact: text,
    our_number: text,
    our_number_name: text,
    sender_name: text,
    provider_conversation_id: text,
    media_json: text,
    from_address: text,
    to_address: text,
    body: text,
    status: text,
    error_code: int,
    error_message: text,
    num_media: int,
    num_segments: int,
    price: z.number().nullish(),
    price_unit: text,
    messaging_service_sid: text,
    sent_at: ts.nullish(),
    date_sent: ts.nullish(),
    created_at: ts.nullish(),
    status_updated_at: ts.nullish(),
    /** When Message Hub last changed the row: newer replaces older, older is ignored. */
    row_updated_at: ts,
});
// A parsed group ("undisclosed-recipients:;") can come without an address: keep the email.
const address = z.object({ address: z.string().nullish(), name: z.string().nullish() }).loose();
/** One `hub_emails` row: an email with at least one client on it, keyed by `message_key`. */
export const feedEmailRowSchema = z.object({
    // Capped so the key fits Postgres's b-tree index whatever its characters (4 bytes each at most).
    message_key: z.string().min(1).max(500),
    rfc_message_id: text,
    thread_ref: text,
    sent_at: ts.nullish(),
    subject: text,
    from_address: text,
    from_name: text,
    to_list: z.array(address).nullish(),
    cc_list: z.array(address).nullish(),
    bcc_list: z.array(address).nullish(),
    direction: z.enum(['inbound', 'outbound']),
    client_addresses: z.array(z.string()).nullish(),
    body_text: text,
    body_truncated: z.boolean().nullish(),
    snippet: text,
    attachment_count: int,
    mailboxes: z.array(z.looseObject({})).nullish(),
    row_updated_at: ts,
});
/**
 * A batch is checked row by row, not as a whole (Hyller's review of PR #108): a whole-batch schema
 * refused every row for one bad one, and Supabase resent the same refused batch forever, stalling
 * every later message. So `rows` is only an array here; the handler validates each row with
 * `feedTextRowSchema` / `feedEmailRowSchema`, stores the good ones, and reports the rest in
 * `rejections` (and in `message_feed_rejections`). `stopped: true` (with no rows) is the job's
 * last call when it is switched off, so System Health shows the feed as off rather than stale.
 */
const batchSchema = z
    .object({
    rows: z.array(z.unknown()).max(MESSAGE_FEED_MAX_ROWS),
    stopped: z.boolean().optional(),
})
    .refine((b) => !b.stopped || b.rows.length === 0, {
    message: 'a stopped batch carries no rows',
    path: ['rows'],
});
export const feedTextBatchSchema = batchSchema;
export const feedEmailBatchSchema = batchSchema;
/**
 * How far ahead of the CRM's clock a `row_updated_at` may be. A version far in the future would
 * win every later comparison and freeze the message (review finding); a few minutes of clock skew
 * between Supabase and the CRM is allowed.
 */
export const FEED_MAX_CLOCK_SKEW_MS = 5 * 60 * 1000;
/** One refused row: where it was in the batch, its key when it had one, and why (field names only). */
export const feedRejectionSchema = z.object({
    index: z.number().int(),
    key: z.string().nullable(),
    reasons: z.array(z.string()),
});
/** What a batch did. A resent or older row is `unchanged`; an empty batch is the heartbeat. */
export const feedBatchResultSchema = z.object({
    inserted: z.number().int(),
    updated: z.number().int(),
    unchanged: z.number().int(),
    rejected: z.number().int(),
    rejections: z.array(feedRejectionSchema),
});
/**
 * Why a row was refused, as `<field>: <zod issue code>`: never the value, which can be a phone
 * number, an address or a message body.
 */
export function rejectionReasons(error) {
    return [...new Set(error.issues.map((i) => `${i.path.join('.') || '(row)'}: ${i.code}`))].sort();
}
/**
 * A copy of a row with every U+0000 removed from its strings, keys included: Postgres refuses it
 * in text and jsonb, so one would fail the whole write (the history import met it, TS-959).
 */
export function withoutNul(value) {
    if (typeof value === 'string')
        return value.includes('\u0000') ? value.replaceAll('\u0000', '') : value;
    if (Array.isArray(value))
        return value.map(withoutNul);
    if (value && typeof value === 'object') {
        return Object.fromEntries(Object.entries(value).map(([k, v]) => [k.replaceAll('\u0000', ''), withoutNul(v)]));
    }
    return value;
}
/** Trimmed and lower-cased like `contacts.primary_email` (`normaliseEmail`); blanks and repeats dropped. */
export function normaliseAddresses(values) {
    const seen = new Set();
    for (const value of values ?? []) {
        const address = normaliseEmail(value);
        if (address)
            seen.add(address);
    }
    return [...seen].sort();
}
/**
 * A set-aside row as an admin reviews it (review 2 of PR #108): the key and why, never the
 * row's values. Open ones keep the feed `failing` on System Health until a later version is
 * stored or an admin dismisses them (`POST /admin/message-feed/rejections:dismiss`).
 */
export const feedRejectionViewSchema = z.object({
    id: z.uuid(),
    feed: z.enum(['texts', 'emails']),
    key: z.string().nullable(),
    reasons: z.array(z.string()),
    timesRejected: z.number().int(),
    firstRejectedAt: z.iso.datetime({ offset: true }),
    lastRejectedAt: z.iso.datetime({ offset: true }),
});
export const FEED_REJECTIONS_PAGE_MAX = 200;
export const feedRejectionListSchema = z.object({
    items: z.array(feedRejectionViewSchema),
    /** Every open one, of which `items` holds the newest (at most 200). */
    total: z.number().int(),
});
export const feedRejectionDismissSchema = z.object({
    feed: z.enum(['texts', 'emails']),
    /** Only these; omitted = every open one of the feed. */
    ids: z.array(z.uuid()).min(1).max(1000).optional(),
});
export const feedRejectionDismissResultSchema = z.object({ dismissed: z.number().int() });
//# sourceMappingURL=message-feed.js.map